import { AABB3 } from "@base/math/AABB3";
import { Mat4 } from "@base/math/Mat4";
import { MathUtil } from "@base/math/MathUtil";
import { Plane } from "@base/math/Plane";
import { Vec2 } from "@base/math/Vec2";
import { IVec3, Vec3 } from "@base/math/Vec3";
import { gtc, ltc } from "@base/util/Comma";
import { ImmHelper } from "@base/util/Immutable";
import { getKeys, omit, PartialRecord, PropertyNamesWithType } from "@base/util/TypeUtil";
import { Access } from "@biz/definitions/AccessDefinitions";
import { OfferListType } from "@biz/definitions/ArticleDefinitions";
import { FL } from "@biz/definitions/FL";
import { sicherheitsKlemmenMap } from "@biz/definitions/MountDefinitons";
import { Product } from "@biz/definitions/Product";
import { SST } from "@biz/definitions/SST";
import { SSTForm } from "@biz/definitions/SSTForm";
import { CornerType, IDistIn, IDistOut, IPoleData, ISunSailData, PoleIndex, SunSailSides, SunSailType, TwistingSetType } from "@biz/definitions/SunSailDefinitions";
import { getCornerTypeName, getEdgeName, getPointName, getPointNameSmall, getPoleId } from "@biz/definitions/SunSailDefinitions.util";
import { CalcProductLogic } from "@biz/items/CalcProductLogic";
import { CalcSailLogic } from "@biz/items/sunSail/CalcSailLogic";
import { AxleLogic } from "@biz/items/sunSail/logic/AxleLogic";
import { LogicS } from "@biz/items/sunSail/logic/LogicS";
import { PoleLogic } from "@biz/items/sunSail/logic/PoleLogic";
import { Poles } from "@biz/items/sunSail/logic/Poles";
import { SandyLogic } from "@biz/items/sunSail/logic/SandyLogic";
import { SunSailLogic } from "@biz/items/sunSail/logic/SunSailLogic";
import { SunSailTopology } from "@biz/items/sunSail/logic/SunSailTopology";
import { ICalculatedSideResult, ObstacleHelper } from "@biz/items/sunSail/ObstacleHelper";
import { Sail2dPoly } from "@biz/items/sunSail/Sail2dPoly";
import { createInitialPole, createInitialSunSail, fillInitialPole } from "@biz/items/sunSail/SunSailInitial";
import { SunSailReducers } from "@biz/items/sunSail/SunSailReducers";
import { ILocaContext } from "@biz/language/LocaContext";
import { ArticleLogic } from "@biz/logic/ArticleLogic";
import { Measure } from "@biz/logic/Measure";
import { FieldErrorMap } from "@biz/models/FieldErrorMap";
import { ISail2dAMCXBaseForm, ISail2dCSXSTwinBaseForm, ISail2dFixedForm, ISail2dForm, ISail2dObstacles, ISail2dSandy, SailCoilUpType } from "@biz/models/Sail2dDefinitions";
import { PD } from "@biz/PD";
import { ICalcContext, ICalculatedProductResult, IProjectCheckError, IProjectInfo, ProductData, ProjectCheckErrorType, ProjectInfoPriority } from "@biz/projectCalculation/ProjectDefinitions";
import { ISCPole } from "@biz/sc/sunSail/ISCSunSailData";

export const maxSail2dSideLength = 25000;

type FormResult = "error" | "success" | "incomplete";

function get(val: number | undefined, defaultValue: number): number {
  return val !== undefined ? val : defaultValue;
}

export interface IRef<T> {
  current: T;
}

interface IInputInOut {
  distanceIn?: number;
  distanceOut?: number;
  maxTotalDistanceOut: number;
  maxAllowedDistanceOut: number;
  maxDistanceIn: number;
  minDistanceIn: number;
  inOut: "in" | "out" | "none";
  poleIndex: PoleIndex;
}

export class CalcSail2dLogic {
  static calc2d(sunSail: ISail2dForm, slc: ICalcContext): ICalculatedProductResult {
    const { lc } = slc;

    if (sunSail.productType !== "sunSail2d")
      throw new Error("Product2dForm supports sunSails only!!!")

    const { ssd, groupsValid } = CalcSail2dLogic.calcSunSailData(sunSail, slc);


    CalcSailLogic.addArticles(slc, ssd);

    CalcSailLogic.checkMaxSunSailArea(ssd, slc);
    CalcSail2dLogic.checkNettoAxleLength(ssd, slc, sunSail);

    if (groupsValid) {
      CalcSailLogic.checkAlignDistOut(slc, ssd);
      CalcSailLogic.checkMaxNetSideLength(slc, ssd);
      CalcSailLogic.checkMinAngles(slc, ssd);
      CalcSail2dLogic.checkMaxDistInOut(slc, ssd, sunSail);
      CalcSailLogic.checkAngles(slc, ssd.sunSailType, Poles.getCount(ssd), SunSailLogic.calcNetPositionGroups(ssd, {}));
      CalcSailLogic.checkSideRatio(ssd, slc.errors, lc, sunSail);
      CalcSail2dLogic.checkPointsOnAxleDist(slc, ssd);
      CalcSailLogic.checkCSPolePositions(slc, ssd);
      CalcSailLogic.checkPylons(ssd, slc.errors, lc.t);

      if (ssd.securityCheckOptions?.ignoreMinDistIn)
        CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, "net", lc.t("Rule_IgnoreMinDistIn"));

      if (SunSailLogic.isHighestPointRequired(sunSail.sunSailType, sunSail.poleCount) && !sunSail.highestPoint) {
        CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Error, "product", lc.t("Rule_HighestPoint_Required")); // https://plaspack.atlassian.net/browse/APPSK-123
      }
      CalcSail2dLogic.checkObstacles(slc, ssd);

      CalcSail2dLogic.doInfos2d(slc, ssd);
    }

    CalcSailLogic.checkOperationDefelectionPoint(slc, ssd);
    CalcSailLogic.checkSandy(slc, ssd);

    const hasClothAndColor = CalcProductLogic.checkValidClothData(slc, PD.productToPD(sunSail), FL.fromProduct(sunSail), sunSail.cloth, sunSail.colorId);

    const product = groupsValid ? ssd : undefined;
    const incomplete = !hasClothAndColor || !product;

    const defaultOfferList: OfferListType = (!sunSail.orderType || sunSail.orderType === "S") ? "S" : "H";
    CalcProductLogic.finalizeCalcContext(slc, { incomplete, defaultOfferList, count: sunSail.count });
    return {
      id: sunSail.id,
      pd: PD.productToPD(sunSail),
      infos: slc.infos,
      errors: slc.errors,
      errorMap: FieldErrorMap.errorMapFromErrors(slc.errors || []),
      articles: incomplete ? {} : slc.ruleArticles,
      product,
      // modified: sunSail
    };
  }

  static calcSunSailData(sunSail: ISail2dForm, slc: ICalcContext): { ssd: any; groupsValid: any; } {
    let ssd = createInitialSunSail(sunSail.id, sunSail.sunSailType, sunSail.poleCount, sunSail.version, FL.fromProduct(sunSail), Vec3.ZERO, "mount", slc.articleMap, slc.colorMap, "2d");
    ssd.count = sunSail.count;
    ssd.clothCount = sunSail.clothCount;
    ssd.waterDirections = sunSail.waterDirections;
    ssd.sailBordersType = sunSail.sailBordersType;
    ssd.edges = sunSail.edges;
    ssd.sourceForm = sunSail;
    ssd.notes = sunSail.notes;
    ssd.orderType = sunSail.orderType || "S"
    ssd.securityCheckOptions = sunSail.securityCheckOptions
    ssd.articleSlots = { ...ssd.articleSlots, ...sunSail.articleSlots }
    for (let i = 0; i < Poles.getCount(ssd); i++) { // preset angle to zero? => used for calculations?
      const pole = Poles.getPole(ssd, Poles.toPoleId(i, "corner"));
      if (!SST.is(sunSail.sunSailType, SST.Sandy)) {// https://plaspack.atlassian.net/browse/APP-1529 ? for sandys the ground position is used for calculating the cloth size
        pole.angle = 0;
      }
    }

    slc.powderCoating = ssd.powderCoating;

    ssd = CalcSailLogic.fillCloth(ssd, sunSail.cloth, sunSail.colorId, slc.articleMap)
    ssd = CalcSail2dLogic.fillPylons(ssd, sunSail);
    ssd = CalcSail2dLogic.fillHighestPoint(ssd, sunSail);
    ssd = CalcSail2dLogic.fillCoilUpDeflectionPosition(ssd, sunSail);
    ssd = CalcSail2dLogic.fillCoilUpPole(ssd, sunSail);
    ssd = CalcSail2dLogic.fillCoilUpType(ssd, sunSail);
    ssd = CalcSail2dLogic.fillTwistingSet(ssd, sunSail);
    ssd = CalcSail2dLogic.fillCornerTypes(ssd, sunSail);
    ssd = CalcSail2dLogic.fillCalculationType(ssd, sunSail);
    ssd = CalcSail2dLogic.fillAlignDistOut(ssd, sunSail)

    let result: FormResult = "success";
    if (SSTForm.is(sunSail, SSTForm.AMCXOneFlex)) {
      const ret = CalcSail2dLogic.calcAMCX(slc, sunSail, ssd);
      result = ret.result;
      ssd = ret.ssd;
    } else if (SSTForm.is(sunSail, SSTForm.CsXsTwin)) {
      const ret = CalcSail2dLogic.calcCSXS(slc, sunSail, ssd);
      result = ret.result;
      ssd = ret.ssd;
    } else if (SSTForm.is(sunSail, SSTForm.Fixed)) {
      const ret = CalcSail2dLogic.calcFixed(slc, sunSail, ssd);
      result = ret.result;
      ssd = ret.ssd;
    } else if (SSTForm.is(sunSail, SSTForm.Sandy)) {
      const ret = CalcSail2dLogic.calcSandy(slc, sunSail, ssd);
      result = ret.result;
      ssd = ret.ssd;
    }
    const groupsValid = result === "success";
    return { ssd, groupsValid }
  }

  static checkNettoAxleLength(ssd: ISunSailData, slc: ICalcContext, sunSail: ISail2dForm): void {
    if (AxleLogic.hasAxle(ssd.sunSailType)) {
      const maxAxleLengthNetto = SunSailLogic.getLogicalMinMaxAxle(ssd).max + SunSailLogic.axisLengthAdd(ssd, AxleLogic.getLogicalAxleLengthType(ssd)) - SunSailLogic.axisLengthAdd(ssd, "netto");
      const axleName = SunSailLogic.getAxleName(ssd.sunSailType);
      const nettoAxleLength = SunSailLogic.calcAxleLength(ssd, "netto");

      if (gtc(nettoAxleLength, maxAxleLengthNetto, 3)) {
        CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, "axle", slc.lc.t("Rule_MaxAxleLengthNetto"), { params: Measure.toMM(maxAxleLengthNetto)}).addFields(sunSail, "sunSail", axleName as any);
      }
    }
  }

  static fillAlignDistOut(ssd: ISunSailData, sunSail: ISail2dForm): ISunSailData {
    if (sunSail.alignDistOut !== undefined) {
      ssd = {
        ...ssd,
        alignDistOut: sunSail.alignDistOut
      }
    }
    return ssd;
  }

  static checkObstacles(slc: ICalcContext, ssd: ISunSailData): void {
    const obstaclePositions = ssd.obstaclePositions;
    if (obstaclePositions) {
      const polys = Sail2dPoly.calcSvgPolys(ssd);
      const keys = getKeys(obstaclePositions);
      for (const k of keys) {
        const pos = obstaclePositions[k];
        if (pos) {
          const inside = Sail2dPoly.isPointInPolys(polys, Vec2.fromXZ(pos));
          if (inside)
            CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, k, slc.lc.t("Rule_Obstacle_Inside"))
        }
      }
    }
  }

  private static valuesSet(values: (number | undefined)[]): "all" | "some" | "none" {
    let oneSet = false;
    let allSet = true;
    for (const v of values) {
      if (v === undefined)
        allSet = false;
      else
        oneSet = true;
    }
    return allSet ? "all" : (oneSet ? "some" : "none")
  }

  private static calcTrianglePositions(ab: number, bc: number, ac: number, mulDir: number = 1, mountToAxleDist: number, mountToAxleDist2: number): IVec3[] | undefined {
    ab = ab / 1000;
    bc = bc / 1000;
    ac = ac / 1000;

    const h = SunSailLogic.calcHeightC(ab, bc, ac);
    if (!isNaN(h)) {
      const beta = Math.acos((ab * ab + ac * ac - bc * bc) / (2 * ab * ac)); // calculate vector at Point A
      const sign = beta < Math.PI * 0.5 ? 1 : -1; // calculate sign
      const a = Vec3.from(-mountToAxleDist, 0, 0);
      const s = Math.sqrt(ab * ab - h * h);
      const b = Vec3.from(sign * (Number.isNaN(s) ? 0 : Math.sqrt(ab * ab - h * h)), 0, h * -mulDir);
      const c = Vec3.from(ac + mountToAxleDist2, 0, 0);
      const points = [a, b, c];

      return points;
    }
    return undefined;
  }

  private static rotatePoints(points: IVec3[], rotationY: number): IVec3[] {
    const m = Mat4.muls(Mat4.createRotationY(rotationY));
    const rotatedPoints = points.map((p) => Vec3.applyMatrix4(p, m))
    return rotatedPoints;
  }

  private static fillPylons(ssd: ISunSailData, sunSail: ISail2dForm): ISunSailData {
    const product = sunSail;
    if (product.pylons) {
      for (let i = 0; i < Poles.getCount(ssd); i++) {
        ssd = SunSailReducers.setPylonType(ssd, i, product.pylons[i], { direct: true })
      }
    }
    return ssd;
  }

  private static fillHighestPoint(ssd: ISunSailData, product: ISail2dForm): ISunSailData {
    const defaultHighesPoint = SunSailLogic.getDefaultHighestPoint(product.sunSailType, product.poleCount);
    const highestPoint = product.highestPoint || (defaultHighesPoint !== undefined ? getPointName(defaultHighesPoint) : undefined);
    if (highestPoint !== undefined) {
      ssd = {
        ...ssd,
        form: {
          ...ssd.form,
          highestPoint: getPoleId(highestPoint)
        }
      }
    }
    return ssd;
  }

  private static fillCoilUpDeflectionPosition(ssd: ISunSailData, product: ISail2dForm): ISunSailData {
    let coilUpDeflectionPosition: ISunSailData["coilUpDeflectionPosition"]
    if (SunSailLogic.hasOperatingDeflectionPosition(ssd.sunSailType, Poles.getCount(ssd))) {
      coilUpDeflectionPosition = product.coilUpDeflectionPosition;
      ssd = ImmHelper.setProperty(ssd, "coilUpDeflectionPosition", coilUpDeflectionPosition)
    }
    if (product.sunSailType === SunSailType.SolidayOne && product.coilUpDeflectionPosition) {
      ssd = SunSailReducers.setPoleArticleSlot(ssd, PoleIndex.B, "heightChange", product.coilUpDeflectionPosition === PoleIndex.D && product.coilUpDeflectionOpposite === "heightChange" ? "00E13000052" : undefined)
      ssd = SunSailReducers.setPoleArticleSlot(ssd, PoleIndex.D, "heightChange", product.coilUpDeflectionPosition === PoleIndex.B && product.coilUpDeflectionOpposite === "heightChange" ? "00E13000052" : undefined)
    }
    return ssd;
  }

  private static fillCalculationType(ssd: ISunSailData, product: ISail2dForm): ISunSailData {
    if (product.calculationType) {
      ssd = {
        ...ssd,
        form: {
          ...ssd.form,
          calculationType: product.calculationType
        }
      }
    }
    return ssd;
  }

  private static fillCoilUpPole(ssd: ISunSailData, product: ISail2dForm): ISunSailData {

    if (product.coilUpPole !== undefined) {
      const poleIndex = getPoleId(product.coilUpPole);

      for (let pi = 0; pi < Poles.getCount(ssd); pi++) {
        if (pi !== poleIndex)
          ssd = SunSailReducers.setPoleArticleSlot(ssd, pi, "coilUp", undefined)
        else {
          const coilUps = SunSailLogic.calcPossibleCoilUps(ssd, poleIndex);
          ssd = SunSailReducers.setPoleArticleSlot(ssd, poleIndex, "coilUp", coilUps.length > 0 ? coilUps[0] : undefined)
        }
      }
    }
    return ssd;
  }

  private static fillCoilUpType(ssd: ISunSailData, product: ISail2dForm): ISunSailData {
    const topology = SunSailTopology.calcTopology(ssd.sunSailType, Poles.getCount(ssd));
    const coilUpPoleIndex = SunSailLogic.getSailCoilUpPoleIndex(ssd, topology.groups[0].nonAxleIndices[0])
    const possibleCoilUps = SunSailLogic.calcPossibleCoilUps(ssd, coilUpPoleIndex);

    if (product.coilUpType !== undefined) {
      ssd = {
        ...ssd,
        form: {
          ...ssd.form,
          coilUpType: product.coilUpType
        }
      }

      const materialGroup = SunSailLogic.getPoleMaterialGroup(ssd, coilUpPoleIndex);
      const sicherheitKlemme = materialGroup ? sicherheitsKlemmenMap[materialGroup] : "";

      const articleIds: Record<SailCoilUpType, string> = {
        "clamp": sicherheitKlemme,
        "snap": "00E13001320",
        "winch": possibleCoilUps[0],
      }
      ssd = SunSailReducers.setPoleArticleSlot(ssd, coilUpPoleIndex, "coilUp", articleIds[product.coilUpType])

    }
    return ssd;
  }

  private static fillTwistingSet(ssd: ISunSailData, product: ISail2dForm): ISunSailData {
    if (SunSailLogic.isTwistingSet(ssd.sunSailType, Poles.getCount(ssd))) {
      ssd = SunSailReducers.setTwistingSet(ssd, product.twistingSet !== undefined && product.twistingSet !== TwistingSetType.None);
      ssd = {
        ...ssd,
        form: {
          ...ssd.form,
          twistingSetType: product.twistingSet
        }
      }
    }
    return ssd;
  }

  private static fillCornerTypes(ssd: ISunSailData, sunSail: ISail2dForm): ISunSailData {
    const model = sunSail;
    for (let i = 0; i < Poles.getCount(ssd); i++) {
      const ct = SunSailLogic._calcCornerType(model.cornerTypes?.[i] || CornerType.None, ssd, i);
      ssd = SunSailReducers.setCorner(ssd, i, ct)
    }
    ssd = {
      ...ssd,
      form: {
        ...ssd.form,
      }
    }
    return ssd;
  }

  static calcDistInOut(sunSail: ISail2dForm, ssd: ISunSailData, infos: IProjectInfo[]): ISunSailData {
    if (SSTForm.is(sunSail, SSTForm.CsXsTwin)) {
      if (sunSail.poleCount === 3) {
        const ioC = CalcSail2dLogic.calcIOFromInput(PoleIndex.C, sunSail.outC, sunSail.inC, sunSail.inOutCPriority, ssd);
        ssd = CalcSail2dLogic.fillFromInputInOut(ssd, ioC);
        CalcSail2dLogic.addInOutInfo(infos, ioC);
      } else if (sunSail.poleCount === 4) {
        const ioC = CalcSail2dLogic.calcIOFromInput(PoleIndex.C, sunSail.outC, undefined, "out", ssd);
        const ioD = CalcSail2dLogic.calcIOFromInput(PoleIndex.D, sunSail.outC, undefined, "out", ssd);

        ssd = CalcSail2dLogic.fillFromInputInOut(ssd, ioC);
        ssd = CalcSail2dLogic.fillFromInputInOut(ssd, ioD);

        CalcSail2dLogic.addInOutInfo(infos, ioC);
        CalcSail2dLogic.addInOutInfo(infos, ioD);
      } else if (sunSail.poleCount === 5) {
        const ioC = CalcSail2dLogic.calcIOFromInput(PoleIndex.C, sunSail.outC, undefined, "out", ssd);
        const ioD = CalcSail2dLogic.calcIOFromInput(PoleIndex.D, sunSail.outC, undefined, "out", ssd);

        ssd = CalcSail2dLogic.fillFromInputInOut(ssd, ioC);
        ssd = CalcSail2dLogic.fillFromInputInOut(ssd, ioD);

        CalcSail2dLogic.addInOutInfo(infos, ioC);
        CalcSail2dLogic.addInOutInfo(infos, ioD);

        const ioE = CalcSail2dLogic.calcIOFromInput(PoleIndex.E, sunSail.outE, sunSail.inE, sunSail.inOutEPriority || "none", ssd);
        ssd = CalcSail2dLogic.fillFromInputInOut(ssd, ioE);
        CalcSail2dLogic.addInOutInfo(infos, ioE);
        // {
        //   const ret = CalcSail2dLogic.inputIOToMm(ioE);
        //   sunSail = ImmHelper.setProperty(sunSail, "inE", ret.inValue);
        //   sunSail = ImmHelper.setProperty(sunSail, "outE", ret.outValue);
        // }
      } else if (sunSail.poleCount === 6) {
        const ioC = CalcSail2dLogic.calcIOFromInput(PoleIndex.C, sunSail.outC, undefined, "out", ssd);
        const ioD = CalcSail2dLogic.calcIOFromInput(PoleIndex.D, sunSail.outC, undefined, "out", ssd);

        ssd = CalcSail2dLogic.fillFromInputInOut(ssd, ioC);
        ssd = CalcSail2dLogic.fillFromInputInOut(ssd, ioD);

        CalcSail2dLogic.addInOutInfo(infos, ioC);
        CalcSail2dLogic.addInOutInfo(infos, ioD);

        const ioE = CalcSail2dLogic.calcIOFromInput(PoleIndex.E, sunSail.outE, undefined, "out", ssd);
        const ioF = CalcSail2dLogic.calcIOFromInput(PoleIndex.F, sunSail.outE, undefined, "out", ssd);

        ssd = CalcSail2dLogic.fillFromInputInOut(ssd, ioE);
        ssd = CalcSail2dLogic.fillFromInputInOut(ssd, ioF);

        CalcSail2dLogic.addInOutInfo(infos, ioE);
        CalcSail2dLogic.addInOutInfo(infos, ioF);
      }
    } else if (SSTForm.is(sunSail, SSTForm.AMCXOneFlex) && sunSail.poleCount === 3) {
      const ioB = CalcSail2dLogic.calcIOFromInput(PoleIndex.B, sunSail.outB, sunSail.inB, sunSail.inOutBPriority, ssd);
      ssd = CalcSail2dLogic.fillFromInputInOut(ssd, ioB);
      // {
      //   const ret = CalcSail2dLogic.inputIOToMm(ioB);
      //   sunSail = ImmHelper.setProperty(sunSail, "inB", ret.inValue);
      //   sunSail = ImmHelper.setProperty(sunSail, "outB", ret.outValue);
      // }
      CalcSail2dLogic.addInOutInfo(infos, ioB);
    } else if (SSTForm.is(sunSail, SSTForm.AMCXOneFlex) && sunSail.poleCount === 4) {
      const ioB = CalcSail2dLogic.calcIOFromInput(PoleIndex.B, sunSail.outB, sunSail.inB, sunSail.inOutBPriority, ssd);
      const ioD = CalcSail2dLogic.calcIOFromInput(PoleIndex.D, sunSail.outD, sunSail.inD, sunSail.inOutDPriority || "none", ssd);

      if (sunSail.alignDistOut) CalcSail2dLogic.handleAlignDistOut(ssd, ioB, ioD, sunSail.outBDPriority === "B");

      ssd = CalcSail2dLogic.fillFromInputInOut(ssd, ioB);
      ssd = CalcSail2dLogic.fillFromInputInOut(ssd, ioD);

      CalcSail2dLogic.addInOutInfo(infos, ioB);
      CalcSail2dLogic.addInOutInfo(infos, ioD);
    }
    return ssd;
  }

  static checkMaxDistOut<T extends ISail2dForm, P extends keyof T>(slc: ICalcContext, ssd: ISunSailData, inputModel: T, field: P, poleIndex: PoleIndex): void {
    const pylonType = SunSailLogic.getInfluencingIndirectPylonType(ssd, poleIndex);
    const maxDistOut = SunSailLogic.getMaxAllowedDistOut(ssd.sunSailType, pylonType);
    const pos = getPointNameSmall(poleIndex);
    const val = /*typeof inputModel[field] === "number" ? inputModel[field] :*/ Measure.roundMM(SunSailLogic.calcDistanceOut(ssd, poleIndex));
    if (typeof val === "number") {
      if (gtc(val, maxDistOut * 1000, 0) /*&& CalcSail2dLogic.hasPriority(inputModel, poleIndex, "out")*/)
        CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, pos, slc.lc.t("Rule_MaxDistOut"), { params: [Measure.toMM(maxDistOut), val.toFixed(0)]}).addFields(inputModel, "sunSail", field);
    }
  }

  static checkMinDistIn<T extends ISail2dForm, P extends keyof T>(slc: ICalcContext, ssd: ISunSailData, inputModel: T, field: P, poleIndex: PoleIndex, groupIndex: number): void {
    if (inputModel.securityCheckOptions?.ignoreMinDistIn)
      return;

    const minAllowedDistIn = SunSailLogic.getMinAllowedDistIn(ssd, poleIndex);
    const pos = getPointNameSmall(poleIndex);
    const val = typeof inputModel[field] === "number" ? inputModel[field] : SunSailLogic.calcDistanceInForClothIndex(ssd, poleIndex, { clothIndex: groupIndex }) * 1000;
    if (typeof val === "number") {
      if (ltc(val, minAllowedDistIn * 1000, 0) && CalcSail2dLogic.hasPriority(inputModel, poleIndex, "in")) {
        CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, pos, slc.lc.t("Rule_MinDistIn"), { params: [Measure.toMM(minAllowedDistIn), val.toFixed(0)]}).addFields(inputModel, "sunSail", field);
      }
    }
  }

  static checkMaxDistIn<T extends ISail2dForm, P extends keyof T>(slc: ICalcContext, ssd: ISunSailData, inputModel: T, field: P, poleIndex: PoleIndex, groupIndex: number): void {
    // const val = (inputModel as any)[field];
    const val = Measure.roundMM(SunSailLogic.calcDistanceInForClothIndex(ssd, poleIndex, { clothIndex: groupIndex }));

    if (typeof val === "number") {
      const pos = getPointNameSmall(poleIndex);
      const minDistIn = SunSailLogic.getMinAllowedDistIn(ssd, poleIndex);
      if (!inputModel.securityCheckOptions?.ignoreMinDistIn) {
        if (ltc(val, minDistIn * 1000, 0) /*&& CalcSail2dLogic.hasPriority(inputModel, poleIndex, "in")*/)
          CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, pos, slc.lc.t("Rule_MinDistIn"), { params: [Measure.toMM(minDistIn), val.toFixed(0)]}).addFields(inputModel, "sunSail", field);
      }
      if (PoleLogic.isAxle(ssd.sunSailType, poleIndex)) {
        const nettoAxleLength = SunSailLogic.calcAxleLength(ssd, "logical");
        const maxAllowedDistanceIn = SunSailLogic.getMaxAllowedDistInAxle(ssd.sunSailType, nettoAxleLength);
        if (gtc(val, maxAllowedDistanceIn.hard * 1000, 0)) {
          CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Error, pos, slc.lc.t("Rule_MaxDistIn_Axle_Hard"), { params: [Measure.toMM(maxAllowedDistanceIn.hard), val]}).addFields(inputModel, "sunSail", field);
        } else if (gtc(val, maxAllowedDistanceIn.soft * 1000, 0)) {
          CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, pos, slc.lc.t("Rule_MaxDistIn_Axle_Soft"), { params: [Measure.toMM(maxAllowedDistanceIn.soft), val]}).addFields(inputModel, "sunSail", field);
        }
      } else {
        const maxDistInNon = SunSailLogic.getMaxAllowedDistInNonAxle(ssd.sunSailType);
        if (gtc(val, maxDistInNon * 1000, 0) /*&& CalcSail2dLogic.hasPriority(inputModel, poleIndex, "in")*/) {
          CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, pos, slc.lc.t("Rule_MaxDistIn"), { params: [Measure.toMM(maxDistInNon), val]}).addFields(inputModel, "sunSail", field);
        }
      }
    }
  }

  static checkMaxDistInOut(slc: ICalcContext, ssd: ISunSailData, inputModel: ISail2dForm): void {
    if (SSTForm.is(inputModel, SSTForm.Fixed)) {
      if (inputModel.yA === undefined) CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.FatalError, "A", slc.lc.t("Error_Select_Missing_DistIn")).addFields(inputModel, "sunSail", "yA");
      if (inputModel.yB === undefined) CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.FatalError, "B", slc.lc.t("Error_Select_Missing_DistIn")).addFields(inputModel, "sunSail", "yB");
      if (inputModel.yC === undefined) CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.FatalError, "C", slc.lc.t("Error_Select_Missing_DistIn")).addFields(inputModel, "sunSail", "yC");
      if (inputModel.poleCount === 4) {
        if (inputModel.yD === undefined) CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.FatalError, "D", slc.lc.t("Error_Select_Missing_DistIn")).addFields(inputModel, "sunSail", "yD");
      }
    }

    if (SSTForm.is(inputModel, SSTForm.Fixed)) {
      CalcSail2dLogic.checkMinDistIn(slc, ssd, inputModel, "yA", PoleIndex.A, 0);
      CalcSail2dLogic.checkMaxDistIn(slc, ssd, inputModel, "yA", PoleIndex.A, 0);

      CalcSail2dLogic.checkMinDistIn(slc, ssd, inputModel, "yB", PoleIndex.B, 0);
      CalcSail2dLogic.checkMaxDistIn(slc, ssd, inputModel, "yB", PoleIndex.B, 0);

      CalcSail2dLogic.checkMinDistIn(slc, ssd, inputModel, "yC", PoleIndex.C, 0);
      CalcSail2dLogic.checkMaxDistIn(slc, ssd, inputModel, "yC", PoleIndex.C, 0);

      if (inputModel.poleCount === 4) {
        CalcSail2dLogic.checkMinDistIn(slc, ssd, inputModel, "yD", PoleIndex.D, 0);
        CalcSail2dLogic.checkMaxDistIn(slc, ssd, inputModel, "yD", PoleIndex.D, 0);
      }
    }

    if (SSTForm.is(inputModel, SSTForm.AMCXOneFlex)) {
      CalcSail2dLogic.checkMaxDistOut(slc, ssd, inputModel, "outB", PoleIndex.B);
      CalcSail2dLogic.checkMaxDistIn(slc, ssd, inputModel, "inB", PoleIndex.B, 0);

      if (inputModel.poleCount === 4) {
        CalcSail2dLogic.checkMaxDistOut(slc, ssd, inputModel, "outD", PoleIndex.D);
        CalcSail2dLogic.checkMaxDistIn(slc, ssd, inputModel, "inD", PoleIndex.D, 1);
      }

      {
        if (AxleLogic.hasAxle(ssd.sunSailType)) {
          CalcSail2dLogic.checkMinDistIn(slc, ssd, inputModel, "yAB", PoleIndex.A, 0);
          CalcSail2dLogic.checkMinDistIn(slc, ssd, inputModel, "yCB", PoleIndex.C, 0);

          CalcSail2dLogic.checkMaxDistIn(slc, ssd, inputModel, "yAB", PoleIndex.A, 0);
          CalcSail2dLogic.checkMaxDistIn(slc, ssd, inputModel, "yCB", PoleIndex.C, 0);

          if (inputModel.poleCount === 4) {
            CalcSail2dLogic.checkMinDistIn(slc, ssd, inputModel, "yAD", PoleIndex.A, 1);
            CalcSail2dLogic.checkMinDistIn(slc, ssd, inputModel, "yCD", PoleIndex.C, 1);

            CalcSail2dLogic.checkMaxDistIn(slc, ssd, inputModel, "yAD", PoleIndex.A, 1);
            CalcSail2dLogic.checkMaxDistIn(slc, ssd, inputModel, "yCD", PoleIndex.C, 1);
          }
        }
      }

      if (inputModel.poleCount === 4) {
        const coilUpPole = ssd.poles.findIndex((_p, pi) => !!SunSailLogic.getSailCoilUpArticle(ssd, pi))
        if (coilUpPole !== -1) {
          const otherPole = Poles.getOtherPole(ssd.sunSailType, coilUpPole);
          const distOutCoilUp = SunSailLogic.calcDistanceOut(ssd, coilUpPole);
          const distOutOther = SunSailLogic.calcDistanceOut(ssd, otherPole);
          if (ltc(distOutCoilUp, distOutOther, 2))
            CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, getPointName(coilUpPole), slc.lc.t("Rule_DistOut_CoilUp"), { params: [distOutCoilUp.toFixed(2), distOutOther.toFixed(2)]}).addFields(
              inputModel,
              "sunSail",
              "outB",
              "outD"
            );
        }
      }
    }

    if (SSTForm.is(inputModel, SSTForm.CsXsTwin)) {
      CalcSail2dLogic.checkMaxDistOut(slc, ssd, inputModel, "outC", PoleIndex.C);
      CalcSail2dLogic.checkMinDistIn(slc, ssd, inputModel, "inC", PoleIndex.C, 0);
      CalcSail2dLogic.checkMaxDistIn(slc, ssd, inputModel, "inC", PoleIndex.C, 0);

      CalcSail2dLogic.checkMinDistIn(slc, ssd, inputModel, "yAC", PoleIndex.A, 0);
      CalcSail2dLogic.checkMinDistIn(slc, ssd, inputModel, "yBC", PoleIndex.B, 0);

      CalcSail2dLogic.checkMaxDistIn(slc, ssd, inputModel, "yAC", PoleIndex.A, 0);
      CalcSail2dLogic.checkMaxDistIn(slc, ssd, inputModel, "yBC", PoleIndex.B, 0);

      if (inputModel.poleCount >= 5) {
        CalcSail2dLogic.checkMinDistIn(slc, ssd, inputModel, "yAE", PoleIndex.A, 1);
        CalcSail2dLogic.checkMinDistIn(slc, ssd, inputModel, "yBE", PoleIndex.B, 1);

        CalcSail2dLogic.checkMaxDistIn(slc, ssd, inputModel, "yAE", PoleIndex.A, 1);
        CalcSail2dLogic.checkMaxDistIn(slc, ssd, inputModel, "yBE", PoleIndex.B, 1);

        CalcSail2dLogic.checkMaxDistOut(slc, ssd, inputModel, "outE", PoleIndex.E);
        CalcSail2dLogic.checkMinDistIn(slc, ssd, inputModel, "inE", PoleIndex.E, 1);
        CalcSail2dLogic.checkMaxDistIn(slc, ssd, inputModel, "inE", PoleIndex.E, 1);
      }
    }
  }

  static calcAMCX(slc: ICalcContext, _sunSail: ISail2dAMCXBaseForm, ssd: ISunSailData): { result: FormResult, ssd: ISunSailData } {
    const sunSail = _sunSail;

    ssd = ImmHelper.setProperty(ssd, "axleType", _sunSail.axleType);

    for (let i = 0; i < SunSailTopology.calcTopology(_sunSail.sunSailType, _sunSail.poleCount).groups.length; i++) {
      const ret = CalcSail2dLogic.fillTrianglePointsAMCXF(i, sunSail, ssd, slc.errors, slc.lc);
      if (ret.result !== "success")
        return ret;
      ssd = ret.ssd;
    }

    if (_sunSail.obstacles) {
      const obstacleKeys = getKeys(_sunSail.obstacles);
      for (const k of obstacleKeys) {
        const obstacle = _sunSail.obstacles[k];
        if (obstacle) {
          const result = ObstacleHelper.calcObstaclePosition(obstacle, _sunSail.AC, slc.lc)
          if (result.value) {
            if (!ssd.obstaclePositions)
              ssd = ImmHelper.setProperty(ssd, "obstaclePositions", {});
            ssd = ImmHelper.setPropertyNonNullable(ssd, "obstaclePositions", k, result.value)
          }
        }
      }
    }

    // // calculate net points
    ssd = CalcSail2dLogic.fillDistanceIn(ssd, PoleIndex.A, get(sunSail.yAB, 0) * 0.001, sunSail.poleCount === 4 ? get(sunSail.yAD, 0) * 0.001 : undefined);
    ssd = CalcSail2dLogic.fillDistanceIn(ssd, PoleIndex.C, get(sunSail.yCB, 0) * 0.001, sunSail.poleCount === 4 ? get(sunSail.yCD, 0) * 0.001 : undefined);

    // CalcSail2dLogic.checkDifferent(sunSail, _sunSail, "a");
    ssd = CalcSail2dLogic.calcDistInOut(sunSail, ssd, slc.infos);
    // CalcSail2dLogic.checkDifferent(sunSail, _sunSail, "b");

    return { result: "success", ssd };
  }

  static calcTwinSecondAxleDiff(sunSail: ISail2dCSXSTwinBaseForm): number {
    if (sunSail.AB2 && sunSail.AB && sunSail.AB !== sunSail.AB2) {
      const secondAxleDiff = (sunSail.AB2 - sunSail.AB) / 1000;
      return secondAxleDiff;
    }
    return 0;
  }

  static calcCSXS(slc: ICalcContext, sunSail: ISail2dCSXSTwinBaseForm, ssd: ISunSailData): { result: FormResult, ssd: ISunSailData } {
    ssd = ImmHelper.setProperty(ssd, "axleType", sunSail.axleType);

    // influences fillQuadPointsCSXS (calcAllowedRegion)
    ssd = CalcSail2dLogic.fillDistanceIn(ssd, PoleIndex.A, get(sunSail.yAC, 0) * 0.001, sunSail.poleCount >= 5 ? get(sunSail.yAE, 0) * 0.001 : undefined);
    ssd = CalcSail2dLogic.fillDistanceIn(ssd, PoleIndex.B, get(sunSail.yBC, 0) * 0.001, sunSail.poleCount >= 5 ? get(sunSail.yBE, 0) * 0.001 : undefined);

    if (sunSail.poleCount === 3) {
      const ret = CalcSail2dLogic.fillTrianglePointsCSXS(0, sunSail, ssd, slc.errors, slc.lc);
      if (ret.result !== "success")
        return ret;
      ssd = ret.ssd;
    } else if (sunSail.poleCount >= 4) {
      // const ret = CalcSail2dLogic.fillQuadPointsCSXS(0, sunSail, ssd, errors);
      const ret = CalcSail2dLogic.fillQuadPointsCSXS(0, sunSail, ssd, slc.errors, slc.lc);
      if (ret.result !== "success")
        return ret;
      ssd = ret.ssd;
    }

    if (sunSail.poleCount > 4) {
      const secondAxleDiff = CalcSail2dLogic.calcTwinSecondAxleDiff(sunSail);
      if (secondAxleDiff) {
        ssd = {
          ...ssd,
          form: {
            ...ssd.form,
            secondAxleDiff
          }
        }
      }

      if (sunSail.poleCount === 5) {
        const ret = CalcSail2dLogic.fillTrianglePointsCSXS(1, sunSail, ssd, slc.errors, slc.lc);
        if (ret.result !== "success")
          return ret;
        ssd = ret.ssd;
      } else if (sunSail.poleCount === 6) {
        const ret = CalcSail2dLogic.fillQuadPointsCSXS(1, sunSail, ssd, slc.errors, slc.lc);
        if (ret.result !== "success")
          return ret;
        ssd = ret.ssd;
      }
    }

    const pp1Possible = SunSailLogic.isPolyPositioningPossible(sunSail.sunSailType, sunSail.poleCount, 0)
    if (pp1Possible) {
      const polyPositioning: boolean[] = [];
      polyPositioning.push(SunSailLogic.isPolyPositioningEnabled(sunSail.sunSailType, sunSail.poleCount, 0, sunSail.cs4PositionMode?.[0] === "flexible"));
      if (SunSailLogic.isPolyPositioningPossible(sunSail.sunSailType, sunSail.poleCount, 1))
        polyPositioning.push(SunSailLogic.isPolyPositioningEnabled(sunSail.sunSailType, sunSail.poleCount, 1, sunSail.cs4PositionMode?.[1] === "flexible"));

      ssd = {
        ...ssd,
        polyPositioning
      }
    }

    ssd = CalcSail2dLogic.calcDistInOut(sunSail, ssd, slc.infos);

    return { result: "success", ssd };
  }

  static calcFixed(slc: ICalcContext, sunSail: ISail2dFixedForm, ssd: ISunSailData): { result: FormResult, ssd: ISunSailData } {
    if (sunSail.inputMode === "rectangle") {
      {
        const ret = CalcSail2dLogic.fillTrianglePointsAMCXF(0, sunSail, ssd, slc.errors, slc.lc);
        if (ret.result !== "success")
          return ret;
        ssd = ret.ssd;
      }
      if (sunSail.poleCount >= 4) {
        const ret = CalcSail2dLogic.fillTrianglePointsAMCXF(1, sunSail, ssd, slc.errors, slc.lc);
        if (ret.result !== "success")
          return ret;
        ssd = ret.ssd;
      }
    } else {
      {
        const ret = CalcSail2dLogic.fillTrianglePointsAMCXF(0, sunSail, ssd, slc.errors, slc.lc);
        if (ret.result !== "success")
          return ret;
        ssd = ret.ssd;
      }
      if (sunSail.poleCount >= 4) {
        const ret = CalcSail2dLogic.fillTrianglePointsAMCXF(1, sunSail, ssd, slc.errors, slc.lc);
        if (ret.result !== "success")
          return ret;
        ssd = ret.ssd;
      }
    }

    ssd = CalcSail2dLogic.fillDistanceIn(ssd, PoleIndex.A, get(sunSail.yA, 0) * 0.001);
    ssd = CalcSail2dLogic.fillDistanceIn(ssd, PoleIndex.B, get(sunSail.yB, 0) * 0.001);
    ssd = CalcSail2dLogic.fillDistanceIn(ssd, PoleIndex.C, get(sunSail.yC, 0) * 0.001);
    if (sunSail.poleCount === 4)
      ssd = CalcSail2dLogic.fillDistanceIn(ssd, PoleIndex.D, get(sunSail.yD, 0) * 0.001);

    ssd = CalcSail2dLogic.calcDistInOut(sunSail, ssd, slc.infos);



    return { result: "success", ssd }
  }

  static calcSandy(slc: ICalcContext, sunSail: ISail2dSandy, ssd: ISunSailData): { result: FormResult, ssd: ISunSailData } {
    const { mount, halfVecs, errors, centerPoints } = SandyLogic.calcPositionModePoints(sunSail); //SandyLogic.calcGroundPoints2d(sunSail)
    if (errors?.length)
      return { result: "error", ssd };

    ssd = SunSailReducers.setPolePositionsDirect(ssd, mount.map((p) => Vec3.fromXY(p)));
    ssd = SunSailReducers.setPoleHalfVecs(ssd, halfVecs.map((p) => Vec3.fromXY(p)));
    ssd.positionMode = "mount";
    // if (sunSail.positionMode) ssd.positionMode = sunSail.positionMode;

    if (centerPoints.length) {
      const polys = Sail2dPoly.calcSvgPolys(ssd);
      ssd = ImmHelper.setProperty(ssd, "centerPoles", centerPoints.map((cp, i) => {
        if (!cp)
          CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.FatalError, getPointName(Poles.toPoleId(i, "center")), slc.lc.t("Error_CenterPole_Triangulation_Short"))
        else if (!Sail2dPoly.isPointInPolys(polys, cp)) {
          CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.FatalError, getPointName(Poles.toPoleId(i, "center")), slc.lc.t("Error_CenterPole_Outside_Poly"))
        }

        return createInitialPole(ssd, Poles.getCount(ssd), Poles.toPoleId(i, "center"), cp ? Vec3.fromXY(cp) : Vec3.from(Number.NaN, Number.NaN, Number.NaN), sunSail.featureLevel)
      }));

      ssd = ImmHelper.setProperty(ssd, "centerPoles", ssd.centerPoles!.map((_cp, i) => {
        return CalcSail2dLogic.mergePoleData(fillInitialPole(ssd, Poles.toPoleId(i, "center")), sunSail.centerPoles![i]);
      }));
    }
    ssd = ImmHelper.setProperty(ssd, "patches", sunSail.patches);

    const poleIds = Poles.getPoleIdsFromCount(Poles.getCount(ssd, "corner"), "corner");
    for (const poleId of poleIds) {
      const sourcePole = Poles.getPole(sunSail, poleId);
      const targetPole = Poles.getPole(ssd, poleId);
      const mergedPole = this.mergePoleData(targetPole, sourcePole)
      // mergedPole.angle = 0; // https://plaspack.atlassian.net/browse/APP-1529 ?
      ssd = ImmHelper.setProperty(ssd, "poles", poleId, mergedPole)
    }

    return { result: "success", ssd }
  }

  static mergePoleData(target: IPoleData, _source: ISCPole): IPoleData {
    const source = omit(_source, "centerPoleTriangulation")
    const ret: IPoleData = { ...target };
    for (const k of getKeys(source)) {
      (ret as any)[k] = source[k] as any;
    }
    return ret;
  }

  static fillFromInputInOut(ssd: ISunSailData, input: IInputInOut): ISunSailData {
    if (input.inOut === "out")
      return CalcSail2dLogic.fillDistanceOut(ssd, input.poleIndex, input.distanceOut ? input.distanceOut : 0);
    else {
      return CalcSail2dLogic.fillDistanceIn(ssd, input.poleIndex, input.distanceIn ? input.distanceIn : 0);
    }
  }

  static fillDistanceOut(ssd: ISunSailData, pi: PoleIndex, out: number): ISunSailData {
    const dist: IDistOut = {
      type: "out",
      val: out
    }
    ssd = SunSailReducers.setDist(ssd, pi, dist, { direct: true });
    return ssd;
  }

  static fillDistanceIn(ssd: ISunSailData, pi: PoleIndex, in1: number, in2?: number): ISunSailData {
    const dist: IDistIn = {
      type: "in",
      val: in2 === undefined ? in1 : [in1, in2]
    }
    ssd = SunSailReducers.setDist(ssd, pi, dist, { direct: true });
    return ssd;
  }

  static calcAABBFromPoints(aabb: AABB3, points: IVec3[]): AABB3 {
    aabb.reset();
    aabb.growPoints(points);
    return aabb;
  }

  static calcCSDistanceToFrontLine(ssd: ISunSailData, poleIndex: number): number {
    // const positions = ssd.poles.map((p) => p.position); // ssd.form ? SunSailLogic.calcMountPositions(ssd, 0, undefined) : ssd.poles.map((p) => p.position)
    const mountPositions = SunSailLogic.calcMountPositions(ssd, 0, undefined);

    const axlePos = SunSailLogic.getAxlePos(ssd.sunSailType, mountPositions);
    const frontMountDist = LogicS.getMountDistance(ssd, poleIndex, axlePos)
    const targetPoint = LogicS.calcFrontNetPosition(ssd, poleIndex, SunSailLogic.getKnotOuterDist() + frontMountDist * 0.5, axlePos);
    const targetPlane = Plane.fromNormal(targetPoint, Vec3.dir(axlePos.first, axlePos.second))
    const distDirect = Plane.calcDistanceToPlane(targetPlane, mountPositions[poleIndex]);
    return distDirect;
  }

  static checkPointsOnAxleDist(slc: ICalcContext, ssd: ISunSailData): void {
    const pylonCount = SunSailLogic.calcPylonCount(ssd);
    if (ssd.sunSailType === SunSailType.SolidayC && Poles.getCount(ssd) === 4 && pylonCount <= 1) {
      const factorZ = SunSailLogic.calcFactorZ(ssd)
      if (gtc(factorZ, 20, 2))
        CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, "axle", slc.lc.t("Rule_FactorZ"), { params: [20, factorZ.toFixed(2)]});
      slc.infos.push({
        name: "Δx",
        value: (factorZ * 100).toFixed(1) + "%",
        priority: ProjectInfoPriority.Other,
        access: Access.Soliday,
      });
    }
  }

  /*static checkConcavity(svg: SvgSailModel) {
    const ssl = svg.ssl;
    const inputModel = svg.orderModel.inputModel;
    if (inputModel.sunSailType === SunSailType.Fixed) {
      const maxConcavity = SunSailLogic.getMaxConcavity(ssl.ssd.sunSailType, ssl.ssd.poleCount, ssl.clothType);
      if (inputModel.concavityA.get(maxConcavity) < maxConcavity * 0.5)
        OrderSailLogic.addWarning(ssl, "AB", "Die Konkavität sollte zumindest die Hälfte des maximalen Wertes betragen!").addFields(inputModel, "concavityA");
      if (inputModel.concavityB.get(maxConcavity) < maxConcavity * 0.5)
        OrderSailLogic.addWarning(ssl, "BC", "Die Konkavität sollte zumindest die Hälfte des maximalen Wertes betragen!").addFields(inputModel, "concavityB");
      if (inputModel.concavityC.get(maxConcavity) < maxConcavity * 0.5)
        OrderSailLogic.addWarning(ssl, "AC", "Die Konkavität sollte zumindest die Hälfte des maximalen Wertes betragen!").addFields(inputModel, "concavityC");
    }
  }*/

  static checkCorners(slc: ICalcContext, sunSail: ISail2dForm, ssd: ISunSailData): void {
    if (sunSail.sunSailType === SunSailType.Fixed) {
      const poleCount = Poles.getCount(ssd);
      for (let poleIndex = 0; poleIndex < poleCount; poleIndex++) {
        const cornerType = SunSailLogic.getCornerType(ssd, poleIndex);
        if (ArticleLogic.isCornerForAll(ssd.sunSailType, cornerType)) {
          for (let pi2 = 0; pi2 < poleCount; pi2++) {
            const cornerType2 = SunSailLogic.getCornerType(ssd, pi2);
            if (cornerType2 !== cornerType) {
              CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, getPointName(poleIndex), slc.lc.t("Rule_Corner_Same"), { params: getCornerTypeName(cornerType, slc.lc)});
              return;
            }
          }
          return;
        }
      }
    }
  }

  static doInfos2d(slc: ICalcContext, ssd: ISunSailData): void {
    const infos = slc.infos;
    const lc = slc.lc;

    const infoGroup = { group: "Segel", priority: ProjectInfoPriority.SunSail };
    if (SST.is(ssd.sunSailType, SST.Sandy)) {
      CalcProductLogic.addInfo(infos, lc.t("ArticleInfo_ClothSourceArea"), SandyLogic.calcSandyObbArea(ssd)!.area.toFixed(2) + Measure.m2, { ...infoGroup, access: Access.Monteur });
    }
    CalcProductLogic.addInfo(infos, lc.t("Info_SailArea"), SunSailLogic.calcSunSailAreaCloth(ssd).toFixed(2) + Measure.m2, { ...infoGroup, access: Access.Monteur });
    CalcProductLogic.addInfo(infos, lc.t("Info_SailCircumference"), SunSailLogic.calcSunSailCircumference(ssd).toFixed(2) + "m", infoGroup);

    const poleCount = Poles.getCount(ssd);
    const topology = SunSailTopology.calcTopology(ssd.sunSailType, poleCount);
    const netPositionGroups = SunSailLogic.calcNetPositionGroups(ssd, {});
    const mountPositions = SunSailLogic.calcMountPositions(ssd, 0, undefined);

    for (let gi = 0; gi < topology.groups.length; gi++) {
      const group = topology.groups[gi];
      const netPositions = netPositionGroups[gi];
      for (let i = 0; i < group.poleIndices.length; i++) {
        const fromPoleIndex = group.poleIndices[i];
        const toPoleIndex = group.poleIndices[(i + 1) % group.poleIndices.length];

        const sideIndex = topology.sides.findIndex((v) => v.first === fromPoleIndex && v.second === toPoleIndex);
        if (sideIndex !== -1) {
          const ft = Vec3.len(netPositions[fromPoleIndex], netPositions[toPoleIndex]);
          const name = Poles.getEdgeText(ssd, fromPoleIndex, toPoleIndex) + "_" + gi;
          CalcProductLogic.addInfoLength(infos, name, ft, infoGroup);
        }
      }
    }

    for (let groupIndex = 0; groupIndex < topology.groups.length; groupIndex++) {
      const group = topology.groups[groupIndex];
      const pointName = groupIndex === 0 ? "B" : "D";
      const pointNameSmall = pointName.toLowerCase();

      const angleNames = ["α", "β", "γ", "ð​", "ε", "ζ", "η", "θ", "ι", "κ", "λ", "μ", "ν", "ξ", "ο", "π", "ρ", "σ", "τ", "υ", "φ", "χ", "ψ", "ω"];

      for (let i = 0; i < group.poleIndices.length; i++) {
        const angle = SunSailLogic.calcAngleForIndex(i, group.poleIndices, mountPositions);
        infos.push({
          name: angleNames[i] + pointName,
          value: MathUtil.rad2deg(angle).toFixed(2) + "°",
          priority: ProjectInfoPriority.SunSail,
          access: Access.Soliday,
        });
      }

      for (let i = 0; i < group.poleIndices.length; i++) {
        const angle = SunSailLogic.calcAngleForIndex(i, group.poleIndices, netPositionGroups[groupIndex]);
        infos.push({
          name: angleNames[i] + pointNameSmall,
          value: MathUtil.rad2deg(angle).toFixed(2) + "°",
          priority: ProjectInfoPriority.SunSail,
          access: Access.Soliday,
        });
      }

      if (ssd) {
        const g = topology.groups[groupIndex];
        g.poleIndices.forEach((pi, index) => {
          const next = g.poleIndices[(index + 1) % g.poleIndices.length]
          const edgeName = getEdgeName(pi, next);
          if (topology.edgeInfos[edgeName]?.netBorder) {
            infos.push({
              name: lc.t("Sail_Concavities") + " " + edgeName,
              value: SunSailLogic.getConcavityPercent(ssd, edgeName) + "%",
              priority: ProjectInfoPriority.SunSail,
              access: Access.Soliday,
            });
          }
        });
      }
    }
  }


  private static fillTrianglePointsCSXS(groupIndex: number, inputModel: ISail2dCSXSTwinBaseForm, ssd: ISunSailData, errors: IProjectCheckError[], lc: ILocaContext): { ssd: ISunSailData, result: FormResult } {
    let sides: readonly any[] = [];
    let values: (number | undefined)[] = [];
    if (inputModel.poleCount === 3 && groupIndex === 0) {
      const sides2 = ["AB", "BC", "AC"] as const;
      sides = sides2;
      values = [inputModel[sides2[0]], inputModel[sides2[1]], inputModel[sides2[2]]];
    } else if (inputModel.poleCount === 5 && groupIndex === 1) {
      const sides2 = ["AB2", "BE", "AE"] as const;
      sides = sides2;
      values = [CalcSail2dLogic.calcAxleLen(inputModel, groupIndex), inputModel[sides2[1]], inputModel[sides2[2]]];
    }

    const valuesSet = CalcSail2dLogic.valuesSet(values);
    if (valuesSet === "all") {
      const axleDeltaY = SunSailLogic.calcAxleDeltaY(ssd, true);
      const bc = Math.sqrt(values[1]! ** 2 - axleDeltaY ** 2);
      const ac = Math.sqrt(values[2]! ** 2 - axleDeltaY ** 2);
      const mountToAxleDists = SunSailLogic.calcAxleDists(ssd, SunSailLogic.getMountToNettoAxleDist);
      let positions: IVec3[] | undefined;
      if (inputModel.poleCount === 3) {
        // positions = CalcSail2dLogic.calcTrianglePositions(bc, ac, values[0]!, groupIndex === 1 ? 1 : -1, mountToAxleDists[0], mountToAxleDists[1]);
        positions = CalcSail2dLogic.calcTrianglePositions(ac, bc, values[0]!, groupIndex === 1 ? 1 : -1, mountToAxleDists.first, mountToAxleDists.second);
      } else if (inputModel.poleCount === 5 && groupIndex === 1) {
        positions = CalcSail2dLogic.calcTrianglePositions(ac, bc, values[0]!, groupIndex === 1 ? 1 : -1, mountToAxleDists.first, mountToAxleDists.second);
      } else {
        positions = CalcSail2dLogic.calcTrianglePositions(bc, ac, values[0]!, groupIndex === 1 ? 1 : -1, mountToAxleDists.first, mountToAxleDists.second);
      }
      if (positions && groupIndex === 0) // https://plaspack.atlassian.net/browse/APPSK-46
        positions = CalcSail2dLogic.rotatePoints(positions, Math.PI);

      if (positions) {
        ssd = SunSailReducers.setPolePosition(ssd, 0, positions[0], { direct: true })
        ssd = SunSailReducers.setPolePosition(ssd, 1, positions[2], { direct: true })
        ssd = SunSailReducers.setPolePosition(ssd, groupIndex === 0 ? 2 : 4, positions[1], { direct: true })
      } else {
        CalcProductLogic.addError(errors, ProjectCheckErrorType.FatalError, ("group" + (groupIndex + 1)) as any, lc.t("Error_Triangle_Invalid")).addFields(inputModel, "sunSail", sides[0], sides[1], sides[2]);
        return { ssd, result: "error" };
      }
    }
    return { ssd, result: valuesSet === "all" ? "success" : "incomplete" };
  }

  static calcOtherSide(side: SunSailSides): SunSailSides {
    const otherSideObject: PartialRecord<SunSailSides, SunSailSides> = {
      "AB": "BC",
      "BC": "AB",
      "AD": "CD",
      "CD": "AD"
    }
    const otherSide = otherSideObject[side]!;
    if (!otherSide)
      throw Error("Otherside for " + side + " not defined!")
    return otherSide;
  }

  static calcSideLengthWithObstacles(inputModel: PartialRecord<SunSailSides, number> & { obstacles?: ISail2dObstacles }, side: SunSailSides, lc: ILocaContext): ICalculatedSideResult | undefined {
    const obstacleData = inputModel.obstacles?.[side];
    if (obstacleData) {
      const otherSide = CalcSail2dLogic.calcOtherSide(side);
      const otherSideLength = inputModel.obstacles?.[otherSide] || inputModel[otherSide];
      const axle = inputModel.AC;
      const or = ObstacleHelper.calcObstacleSideLength(obstacleData, axle, otherSideLength, lc);
      return or;
    }
    return { value: inputModel[side] };
  }

  private static fillTrianglePointsAMCXF(groupIndex: number, inputModel: ISail2dAMCXBaseForm | ISail2dFixedForm, ssd: ISunSailData, errors: IProjectCheckError[], lc: ILocaContext): { ssd: ISunSailData, result: FormResult } {
    let sides: readonly any[];
    let _values: (ICalculatedSideResult | undefined)[] = [];
    if (inputModel.inputMode === "rectangle") {
      if (groupIndex === 0) {
        const sides2 = ["AB", "BC", "AC"] as const;
        sides = sides2;
        const c = Math.sqrt((inputModel.AB || 0) ** 2 + (inputModel.BC || 0) ** 2);
        _values = [{ value: inputModel.AB }, { value: inputModel.BC }, { value: c }]
      } else {
        const sides2 = ["CD", "AD", "AC"] as const;
        sides = sides2;
        const c = Math.sqrt((inputModel.AB || 0) ** 2 + (inputModel.BC || 0) ** 2);
        _values = [{ value: inputModel.BC }, { value: inputModel.AB }, { value: c }]
      }
    } else {
      const sides2 = (inputModel.poleCount === 3 || groupIndex === 0) ? ["AB", "BC", "AC"] as const : ["AD", "CD", "AC"] as const;
      sides = sides2;
      _values = [CalcSail2dLogic.calcSideLengthWithObstacles(inputModel, sides2[0], lc), CalcSail2dLogic.calcSideLengthWithObstacles(inputModel, sides2[1], lc), CalcSail2dLogic.calcSideLengthWithObstacles(inputModel, sides2[2], lc)];
    }


    for (let i = 0; i < _values.length; i++) {
      const v = _values[i];
      if (v) {
        if (v.error) {
          CalcProductLogic.addError(errors, ProjectCheckErrorType.FatalError, ("group" + (groupIndex + 1)) as any, v.error).addFields(inputModel, "sunSail", sides[i]);
        }
      }
    }

    const values: (number | undefined)[] = _values.map((v) => v?.value);

    const valuesSet = CalcSail2dLogic.valuesSet(values);
    if (valuesSet === "all") {
      const axleDeltaY = SunSailLogic.calcAxleDeltaY(ssd, true);
      const ab = Math.sqrt(values[0]! ** 2 - axleDeltaY ** 2);
      const ac = Math.sqrt(values[1]! ** 2 - axleDeltaY ** 2);
      const mountToAxleDists = SunSailLogic.calcAxleDists(ssd, SunSailLogic.getMountToNettoAxleDist); // SunSailLogic.getMountToAxleDist);
      const positions = CalcSail2dLogic.calcTrianglePositions(ab, ac, values[2]!, groupIndex === 0 ? 1 : -1, mountToAxleDists.first, mountToAxleDists.second);
      MathUtil.checkNaN(positions, "fillTrianglePointsAMCXF")
      if (positions) {
        ssd = SunSailReducers.setPolePosition(ssd, 0, positions[0], { direct: true })
        ssd = SunSailReducers.setPolePosition(ssd, groupIndex === 0 ? 1 : 3, positions[1], { direct: true })
        ssd = SunSailReducers.setPolePosition(ssd, 2, positions[2], { direct: true })
      } else {
        CalcProductLogic.addError(errors, ProjectCheckErrorType.FatalError, ("group" + (groupIndex + 1)) as any, lc.t("Error_Triangle_Invalid")).addFields(inputModel, "sunSail", sides[0], sides[1], sides[2]);
        return { ssd, result: "error" };
      }
    }
    return { ssd, result: valuesSet === "all" ? "success" : "incomplete" };
  }

  private static calcIOFromInput(ib: PoleIndex, outB: number | undefined, inB: number | undefined, priority: "none" | "out" | "in", ssd: ISunSailData): IInputInOut {
    let distanceIn: number = 0;
    let distanceOut: number = 0;
    let maxTotalDistanceOut: number = 0;
    let maxAllowedDistanceOut: number = 0;
    let maxDistanceIn: number = 0;
    let minDistanceIn: number = 0;

    const axle = SunSailLogic.calcAxlePositions(ssd, ib, SunSailLogic.getMountToLogicalAxleDist);

    if (axle && SunSailLogic.isAxleValid(Vec3.to(axle.first, axle.second))) {
      const total = SunSailLogic.calcMaxDistInOut(ssd, ib);
      maxTotalDistanceOut = total.totalMaxDistOut;
      maxAllowedDistanceOut = total.maxAllowedDistOut; // ??
      maxDistanceIn = total.maxDistIn;
      minDistanceIn = total.minDistIn;
      // const minAllowedDistanceIn = SunSailLogic.calcDistanceInFromOut(maxAllowedDistanceOut, total);
      distanceIn = minDistanceIn; // default
      distanceOut = maxAllowedDistanceOut; // default
      if (priority === "out") {
        if (/*outB &&*/ outB !== undefined) {
          distanceOut = Math.min(outB / 1000, maxAllowedDistanceOut);
          distanceIn = SunSailLogic.calcDistanceInFromOut(distanceOut, total);
        }
      } else if (priority === "in") {
        if (/*inB &&*/ inB !== undefined) {
          distanceIn = MathUtil.clamp(inB / 1000, minDistanceIn, maxDistanceIn);
          distanceOut = SunSailLogic.calcDistanceOutFromIn(distanceIn, total)
        }
      } else if (priority === "none") {
        const pylonType = SunSailLogic.getInfluencingIndirectPylonType(ssd, ib);
        const maxDistOut = SunSailLogic.getMaxAllowedDistOut(ssd.sunSailType, pylonType)
        if (maxDistOut < maxAllowedDistanceOut) {
          distanceOut = maxDistOut;
          distanceIn = SunSailLogic.calcDistanceInFromOut(distanceOut, total);
        }
      }
    }

    return {
      distanceIn,
      distanceOut,
      maxDistanceIn,
      minDistanceIn,
      maxAllowedDistanceOut,
      maxTotalDistanceOut,
      inOut: priority,
      poleIndex: ib,
    };
  }

  private static addInOutInfo(infos: IProjectInfo[], io: IInputInOut): void {
    const infoGroup = { group: "Ein/Auszug", priority: ProjectInfoPriority.IO, access: Access.Dealer };

    const poleName = getPointName(io.poleIndex);
    const poleNameLower = poleName.toLowerCase();
    if (io.distanceOut !== undefined) {
      CalcProductLogic.addInfoLength(infos, "x" + poleNameLower, io.distanceOut, infoGroup);
      CalcProductLogic.addInfoLength(infos, "x" + poleName + "Max", io.maxTotalDistanceOut, infoGroup);
      CalcProductLogic.addInfoLength(infos, "x" + poleName + "MaxAllowed", io.maxAllowedDistanceOut, infoGroup);
    }

    if (io.distanceIn !== undefined) {
      CalcProductLogic.addInfoLength(infos, "y" + poleName, io.distanceIn, infoGroup);
      CalcProductLogic.addInfoLength(infos, "y" + poleName + "Min", io.minDistanceIn, infoGroup);
      CalcProductLogic.addInfoLength(infos, "y" + poleName + "Max", io.maxDistanceIn, infoGroup);
    }
  }

  private static handleAlignDistOut(ssd: ISunSailData, ioFirst: IInputInOut, ioSecond: IInputInOut, priorityFirst: boolean): void {
    const alignedMaxDistOut = Math.min(ioFirst.maxAllowedDistanceOut, ioSecond.maxAllowedDistanceOut);
    if (priorityFirst) {
      ioSecond.distanceOut = Math.min(ioFirst.distanceOut ? ioFirst.distanceOut : 0, alignedMaxDistOut);
      const totalSecond = SunSailLogic.calcTotalMaxDistInOut(ssd, ioSecond.poleIndex);
      ioSecond.distanceIn = SunSailLogic.calcDistanceInFromOut(ioSecond.distanceOut ? ioSecond.distanceOut : 0, totalSecond);
      ioSecond.inOut = "out";
      if (ioFirst.distanceOut) {
        if (ioFirst.distanceOut > alignedMaxDistOut) {
          ioFirst.distanceOut = alignedMaxDistOut;
          ioFirst.inOut = "out";
          const totalFirst = SunSailLogic.calcTotalMaxDistInOut(ssd, ioFirst.poleIndex);
          ioFirst.distanceIn = SunSailLogic.calcDistanceInFromOut(ioFirst.distanceOut ? ioFirst.distanceOut : ioFirst.minDistanceIn, totalFirst);
        }
      }
    } else {
      ioFirst.distanceOut = Math.min(ioSecond.distanceOut ? ioSecond.distanceOut : 0, alignedMaxDistOut);
      const totalFirst = SunSailLogic.calcTotalMaxDistInOut(ssd, ioFirst.poleIndex);
      ioFirst.distanceIn = SunSailLogic.calcDistanceInFromOut(ioFirst.distanceOut ? ioFirst.distanceOut : 0, totalFirst);
      ioFirst.inOut = "out";
      if (ioSecond.distanceOut) {
        if (ioSecond.distanceOut > alignedMaxDistOut) {
          ioSecond.distanceOut = alignedMaxDistOut;
          ioSecond.inOut = "out";
          const totalSecond = SunSailLogic.calcTotalMaxDistInOut(ssd, ioSecond.poleIndex);
          ioSecond.distanceIn = SunSailLogic.calcDistanceInFromOut(ioSecond.distanceOut ? ioSecond.distanceOut : 0, totalSecond);
        }
      }
    }
  }

  // private static inputIOToMm(input: IInputInOut): { inValue: number | undefined; outValue: number | undefined } {
  //   const inValue = input.distanceIn !== undefined ? Measure.roundMM(input.distanceIn) : undefined;
  //   const outValue = input.distanceOut !== undefined ? Measure.roundMM(input.distanceOut) : undefined;
  //   return { inValue, outValue };
  // }

  // private static oneEmpty(...args: any[]): boolean {
  //   for (const a of args)
  //     if (a === undefined)
  //       return true;
  //   return false;
  // }

  // private static allEmpty(...args: any[]): boolean {
  //   for (const a of args)
  //     if (a !== undefined)
  //       return false;
  //   return true;
  // }

  private static fillQuadPointsCSXS(groupIndex: number, sunSail: ISail2dCSXSTwinBaseForm, ssd: ISunSailData, errors: IProjectCheckError[], lc: ILocaContext): {
    ssd: ISunSailData,
    result: FormResult
  } {
    if (sunSail.poleCount < 4) return { ssd, result: "incomplete" };

    // const sides = CalcSail2dLogic.getInputDiagonals(groupIndex);
    // const { ab, ...rest } = sides;
    // const values = mapRecordToArray(rest, (r) => (sunSail as (ISail2dTwin6Form))[r])
    // if (this.allEmpty(...values))
    //   return CalcSail2dLogic.fillQuadPointsCSXSByAxleAndDistOut(groupIndex, sunSail, ssd, errors);
    // return CalcSail2dLogic.fillQuadPointsCSXSByDiagonals(groupIndex, sunSail, ssd, errors);
    if (sunSail.calculationType === "distOut")
      return CalcSail2dLogic.fillQuadPointsCSXSByAxleAndDistOut(groupIndex, sunSail, ssd, errors, lc)
    else
      return CalcSail2dLogic.fillQuadPointsCSXSByDiagonals(groupIndex, sunSail, ssd, errors, lc);
  }

  private static calcAxleLen(sunSail: ISail2dCSXSTwinBaseForm, groupIndex: number): number | undefined {
    if (groupIndex === 1) {
      return sunSail.AB2 || sunSail.AB;
    }
    return sunSail.AB;
  }

  private static fillQuadPointsCSXSByAxleAndDistOut(groupIndex: number, sunSail: ISail2dCSXSTwinBaseForm, ssd: ISunSailData, _errors: IProjectCheckError[], _lc: ILocaContext): {
    ssd: ISunSailData,
    result: FormResult
  } {
    if (sunSail.poleCount < 4) return { ssd, result: "incomplete" };

    const ab = CalcSail2dLogic.getMeter(CalcSail2dLogic.calcAxleLen(sunSail, groupIndex));
    const outC = CalcSail2dLogic.getMeter(groupIndex === 0 ? sunSail.outC : sunSail.outE);

    if (ab === undefined || outC === undefined) return { ssd, result: "incomplete" };

    const mountToAxleDists = SunSailLogic.calcAxleDists(ssd, SunSailLogic.getMountToNettoAxleDist);
    let a: IVec3;
    let b: IVec3;
    // set positions for a,b => needed for calcAllowedRegion
    const axleDiff = groupIndex === 0 ? 0 : CalcSail2dLogic.calcTwinSecondAxleDiff(sunSail);
    if (groupIndex === 0) {
      a = Vec3.from(-mountToAxleDists.first, 0, 0);
      b = Vec3.from(ab + mountToAxleDists.second, 0, 0);
      ssd = SunSailReducers.setPolePosition(ssd, 0, a, { direct: true });
      ssd = SunSailReducers.setPolePosition(ssd, 1, b, { direct: true });
    } else {
      a = Vec3.from(-mountToAxleDists.first - axleDiff * 0.5, 0, 0);
      b = Vec3.from(ab + mountToAxleDists.second - axleDiff * 0.5, 0, 0);
    }

    const isPolyPositioning = SunSailLogic.isPolyPositioningEnabled(sunSail.sunSailType, sunSail.poleCount, groupIndex, sunSail.cs4PositionMode?.[groupIndex] === "flexible");
    const minDistIn = SunSailLogic.getMinAllowedDistInCSXSTwin(sunSail.sunSailType, sunSail.poleCount, groupIndex === 0 ? 2 : 4, isPolyPositioning, sunSail.securityCheckOptions?.ignoreMinDistIn);
    const outCVec = Vec3.from(0, 0, (minDistIn + outC) * (groupIndex === 0 ? 1 : -1));
    const narrowFactor = SunSailLogic.getStaticNarrowFactor(sunSail.sunSailType) * outC;

    const yA = CalcSail2dLogic.getMeter(groupIndex === 0 ? sunSail.yAC : sunSail.yAE) || 0;
    const yB = CalcSail2dLogic.getMeter(groupIndex === 0 ? sunSail.yBC : sunSail.yBE) || 0;
    const frontNetLength = ab - narrowFactor * 2 - yA - yB;
    const knotMid = LogicS.getMountDistanceDirect(frontNetLength) * 0.5;
    let points: IVec3[] = [];
    if (isPolyPositioning) {
      const safety = 0.01;
      const netOffset = SunSailLogic.getKnotOuterDist() + knotMid - SunSailLogic.calcAreaPositioningDistFromNormal(sunSail.sunSailType) - safety;
      const offsetA = Vec3.from(mountToAxleDists.first + narrowFactor + yA + netOffset, 0, 0);
      const offsetB = Vec3.from(mountToAxleDists.second + narrowFactor + yB + netOffset, 0, 0);
      points = [a, b,
        Vec3.sub(Vec3.add(b, outCVec), offsetB),
        Vec3.add(Vec3.add(a, outCVec), offsetA)
      ]
    } else {
      points = [a, b,
        Vec3.from(ab - (narrowFactor + yB + SunSailLogic.getKnotOuterDist() + knotMid) - axleDiff * 0.5, 0, outCVec.z),
        Vec3.from(narrowFactor + yA + SunSailLogic.getKnotOuterDist() + knotMid - axleDiff * 0.5, 0, outCVec.z),
        // Vec3.add(Vec3.add(a, outCVec), offsetA)
      ]
    }

    if (groupIndex === 0) {
      for (let i = 2; i < points.length; i++)
        ssd = SunSailReducers.setPolePosition(ssd, i, points[i], { direct: true });
    } else {
      for (let i = 2; i < points.length; i++)
        ssd = SunSailReducers.setPolePosition(ssd, 2 + i, points[i], { direct: true });
    }

    return { ssd, result: "success" };
  }

  private static getInputDiagonals(groupIndex: number): Record<"ab" | "ad" | "bc" | "ac" | "bd", PropertyNamesWithType<ISail2dCSXSTwinBaseForm, number | undefined>> {
    const side: Record<string, PropertyNamesWithType<ISail2dCSXSTwinBaseForm, number | undefined>> = groupIndex === 0
      ? { ab: "AB", ad: "AD", bc: "BC", ac: "AC", bd: "BD" }
      : { ab: "AB2", ad: "AF", bc: "BE", ac: "AE", bd: "BF" };
    return side;
  }

  private static fillQuadPointsCSXSByDiagonals(groupIndex: number, sunSail: ISail2dCSXSTwinBaseForm, ssd: ISunSailData, errors: IProjectCheckError[], lc: ILocaContext): {
    ssd: ISunSailData,
    result: FormResult
  } {
    const axleDiff = groupIndex === 0 ? 0 : CalcSail2dLogic.calcTwinSecondAxleDiff(sunSail);
    const side = CalcSail2dLogic.getInputDiagonals(groupIndex);

    const ab = CalcSail2dLogic.getMeter(CalcSail2dLogic.calcAxleLen(sunSail, groupIndex));
    const ad = CalcSail2dLogic.getMeter(sunSail[side.ad]);
    const bc = CalcSail2dLogic.getMeter(sunSail[side.bc]);
    const ac = CalcSail2dLogic.getMeter(sunSail[side.ac]);
    const bd = CalcSail2dLogic.getMeter(sunSail[side.bd]);

    if (ab === undefined || ad === undefined || bc === undefined || ac === undefined || bd === undefined) return { ssd, result: "incomplete" };

    const hd = SunSailLogic.calcHeightC(ad, bd, ab);
    if (isNaN(hd)) {
      CalcProductLogic.addError(errors, ProjectCheckErrorType.FatalError, "net", lc.t("Error_Triangle_Invalid")).addFields(sunSail, "sunSail", side.ad, side.bd, side.ab);
      return { ssd, result: "error" };
    }

    const hc = SunSailLogic.calcHeightC(ac, bc, ab);
    if (isNaN(hc)) {
      CalcProductLogic.addError(errors, ProjectCheckErrorType.FatalError, "net", lc.t("Error_Triangle_Invalid")).addFields(sunSail, "sunSail", side.ac, side.bc, side.ab);
      return { ssd, result: "error" };
    }

    const mountToAxleDists = SunSailLogic.calcAxleDists(ssd, SunSailLogic.getMountToNettoAxleDist);
    const a = Vec3.from(-mountToAxleDists.first - axleDiff * 0.5, 0, 0);
    const b = Vec3.from(ab + mountToAxleDists.second - axleDiff * 0.5, 0, 0);
    const points: IVec3[] = [
      a, b,
      Vec3.from(Math.sqrt(ac * ac - hc * hc) - axleDiff * 0.5, 0, hc * (groupIndex === 0 ? 1 : -1)),
      Vec3.from(ab - Math.sqrt(bd * bd - hd * hd) - axleDiff * 0.5, 0, hd * (groupIndex === 0 ? 1 : -1))
    ]

    if (groupIndex === 0) {
      for (let i = 0; i < points.length; i++)
        ssd = SunSailReducers.setPolePosition(ssd, i, points[i], { direct: true });
    } else {
      for (let i = 2; i < points.length; i++)
        ssd = SunSailReducers.setPolePosition(ssd, 2 + i, points[i], { direct: true });
    }

    return { ssd, result: "success" };
  }

  private static getMeter(fm: number | undefined): number | undefined {
    if (fm === undefined) return undefined;
    return fm / 1000;
  }

  // static createProjectPackageFromForm(
  //   dealer: IProjectDealer,
  //   orderInfo: IProjectOrderInfo,
  //   ssds: SunSailData[],
  //   shoppingCart: IShoppingArticle[],
  //   articleMap: Record<string, IArticle>,
  //   getBeosCompany: (id: number) => IBeosCompany | undefined
  // ): IProjectPackage | undefined {
  //   // const company = context.companyList.find((c) => c._id === context.inputForm.dealer.companyId);
  //   const company = getBeosCompany(dealer.companyId);
  //   if (company) {
  //     const project = SunSailProject.create(orderInfo.commission, ssds, shoppingCart);
  //     const pp = SunSailLogicHelper.createProjectPackage(project, company, undefined, articleMap);
  //     return pp;
  //   }
  //   return undefined;
  // }

  public static hasPriorityAMCX(inputModel: ISail2dAMCXBaseForm, poleIndex: number, inOut: "in" | "out"): boolean {
    if (poleIndex === 1) {
      return inputModel.inOutBPriority === inOut && (!inputModel.alignDistOut || (inputModel.poleCount === 4 && inputModel.outBDPriority === "B"));
    } else if (poleIndex === 3 && inputModel.poleCount === 4) {
      return inputModel.inOutDPriority === inOut && (!inputModel.alignDistOut || (inputModel.poleCount === 4 && inputModel.outBDPriority === "D"));
    }
    return true;
  }

  public static hasPriorityCSXS(inputModel: ISail2dCSXSTwinBaseForm, poleIndex: number, inOut: "in" | "out"): boolean {
    if (poleIndex === 2) {
      return inputModel.inOutCPriority === inOut;
    } else if (poleIndex === 4 && inputModel.poleCount === 5) {
      return inputModel.inOutEPriority === inOut;
    }
    return false;
  }

  public static hasPriority(inputModel: ISail2dForm, poleIndex: number, inOut: "in" | "out"): boolean {
    if (SSTForm.is(inputModel, SSTForm.AMCXOneFlex)) {
      return CalcSail2dLogic.hasPriorityAMCX(inputModel, poleIndex, inOut);
    } else if (SSTForm.is(inputModel, SSTForm.CsXsTwin) && (inputModel.poleCount === 3 || inputModel.poleCount === 5)) {
      return CalcSail2dLogic.hasPriorityCSXS(inputModel, poleIndex, inOut);
    }
    return true;
  }

  static getSideLenMM(product: ISunSailData, a: PoleIndex, b: PoleIndex): number {
    const len = Vec3.len(SunSailLogic.getSideVector(product, a, b));
    return Measure.roundMM(len)
  }


  static calcCSXS_CD(product: ProductData | undefined): number | undefined {
    if (Product.isSunSail(product)) {
      if (Poles.getCount(product) >= 4)
        return CalcSail2dLogic.getSideLenMM(product, PoleIndex.C, PoleIndex.D)
    }
    return undefined;
  }

  static calcCSXS_EF(product: ProductData | undefined): number | undefined {
    if (Product.isSunSail(product)) {
      if (Poles.getCount(product) === 6)
        return CalcSail2dLogic.getSideLenMM(product, PoleIndex.E, PoleIndex.F)
    }
    return undefined;
  }

  static calcAMCX_BD(product: ProductData | undefined): number | undefined {
    if (Product.isSunSail(product)) {
      if (Poles.getCount(product) >= 4)
        return CalcSail2dLogic.getSideLenMM(product, PoleIndex.B, PoleIndex.D)
    }
    return undefined;
  }

  static calcAMCX_AC(product: ProductData | undefined): number | undefined {
    if (Product.isSunSail(product)) {
      return CalcSail2dLogic.getSideLenMM(product, PoleIndex.A, PoleIndex.C)
    }
    return undefined;
  }
}

